import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const perks = [
  { icon: 'ri-eye-line', text: 'Stav projektu v reálnom čase - fázy, percento dokončenia' },
  { icon: 'ri-task-line', text: 'Úlohy s termínmi - vždy viete, čo na vás čaká' },
  { icon: 'ri-service-line', text: 'Objednané služby, ceny a dátumy ďalšej fakturácie' },
  { icon: 'ri-bar-chart-2-line', text: 'Mesačné reporty vo formáte PDF na stiahnutie' },
];

export default function PortalTeaser() {
  const { ref, inView } = useInView();

  return (
    <section className="py-28 bg-zinc-950 relative overflow-hidden">
      {/* Background accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(249,115,22,0.07) 0%, transparent 55%)' }}
      />
      <div
        className="absolute right-0 top-0 bottom-0 w-1/2 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 80% 50%, rgba(249,115,22,0.04) 0%, transparent 60%)' }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Left - text */}
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-computer-line text-orange-400 text-sm" />
              <span className="text-orange-400 text-sm font-semibold">Klientský portál</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-5">
              Vždy viete, kde stojí{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
                váš projekt.
              </span>
            </h2>

            <p className="text-zinc-400 text-base leading-relaxed mb-4">
              Žiadne emaily štýlu „a kde to stojíte?". Každý klient Boostio dostane
              vlastný online portál, kde vidí <strong className="text-white">všetko - hneď, sám, odkiaľkoľvek.</strong>
            </p>

            {/* Comparison pill */}
            <div className="flex items-center gap-3 mb-8 p-4 bg-zinc-900/60 border border-zinc-800 rounded-2xl">
              <div className="flex-shrink-0 space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-red-400">
                  <i className="ri-close-circle-line" />
                  <span>Iní: napíšete → čakáte 3 dni → „pracujeme na tom"</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-emerald-400">
                  <i className="ri-checkbox-circle-line" />
                  <span>Boostio: prihlásíte sa → vidíte všetko sami</span>
                </div>
              </div>
            </div>

            {/* Perks */}
            <ul className="space-y-3 mb-8">
              {perks.map((p) => (
                <li key={p.text} className="flex items-start gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-500/10 border border-orange-500/15 flex-shrink-0 mt-0.5">
                    <i className={`${p.icon} text-orange-400 text-sm`} />
                  </div>
                  <span className="text-zinc-300 text-sm leading-relaxed">{p.text}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/klientsky-portal"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-6 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Pozrieť celý portál
                <i className="ri-arrow-right-line" />
              </Link>
              <a
                href="#kontakt"
                onClick={(e) => { e.preventDefault(); document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white font-semibold px-6 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Stať sa klientom
              </a>
            </div>
          </div>

          {/* Right - mini dashboard visual */}
          <div className="relative">
            {/* Glow behind the mockup */}
            <div
              className="absolute inset-0 rounded-3xl pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(249,115,22,0.15) 0%, transparent 65%)' }}
            />

            {/* Mini browser mockup */}
            <div className="relative rounded-2xl overflow-hidden border border-zinc-700/60 bg-zinc-900">
              {/* Browser bar */}
              <div className="bg-zinc-800 px-4 py-2.5 flex items-center gap-2.5 border-b border-zinc-700/60">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/50" />
                </div>
                <div className="flex-1 mx-2 bg-zinc-700/50 rounded px-2.5 py-1 flex items-center gap-1.5">
                  <i className="ri-lock-line text-zinc-600 text-xs" />
                  <span className="text-zinc-600 text-xs font-mono">portal.boostio.sk</span>
                </div>
              </div>

              {/* App content */}
              <div className="p-4 space-y-3 bg-zinc-950">
                {/* Top bar */}
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-white font-bold text-sm">Bratislava Pet Shop</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                      <span className="text-emerald-400 text-xs font-medium">Live - práve aktuálne</span>
                    </div>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg px-2.5 py-1">
                    <span className="text-orange-400 text-xs font-bold">65%</span>
                    <span className="text-zinc-600 text-xs"> dokončené</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-zinc-300 text-xs font-semibold">Redesign e-shopu</span>
                    <span className="bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] px-2 py-0.5 rounded-full font-semibold">V progrese</span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full w-[65%] bg-gradient-to-r from-orange-500 to-amber-400 rounded-full" />
                  </div>
                </div>

                {/* 2 quick cards */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <i className="ri-task-line text-orange-400 text-xs" />
                      <span className="text-zinc-400 text-xs">Úlohy</span>
                    </div>
                    <p className="text-white font-black text-lg leading-none">2</p>
                    <p className="text-orange-400 text-[10px] font-semibold mt-0.5">čakajú na vás</p>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <i className="ri-service-line text-emerald-400 text-xs" />
                      <span className="text-zinc-400 text-xs">Služby</span>
                    </div>
                    <p className="text-white font-black text-lg leading-none">3</p>
                    <p className="text-emerald-400 text-[10px] font-semibold mt-0.5">aktívne</p>
                  </div>
                </div>

                {/* Report row */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-file-chart-line text-orange-400 text-sm" />
                    <div>
                      <p className="text-zinc-200 text-xs font-semibold">Report - február 2025</p>
                      <p className="text-emerald-400 text-[10px] font-semibold">+18 % návštevnosť</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-orange-400 text-[10px] font-semibold">
                    <i className="ri-download-line" />
                    PDF
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-3 -right-3 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <i className="ri-wifi-line" />
              24/7 online
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
